/**
 * OrionDocumentManagement — Admin
 * Gestion de la base de connaissance d'Orion (documents PDF indexés).
 * Miroir de AppDocumentManagement mais pour des PDFs (convention ÉLISFA, accords, etc.)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Bot, Upload, Trash2, RefreshCw, Loader, ChevronDown, ChevronRight, FileText, ExternalLink } from 'lucide-react';
import {
  getHRDocuments, uploadHRDocument, deleteHRDocument, reindexHRDocument,
} from '../../api';

const CATEGORIES = [
  { value: 'convention', label: 'Convention collective' },
  { value: 'accord', label: 'Accord d\'entreprise' },
  { value: 'fiche_poste', label: 'Fiche de poste' },
  { value: 'procedure', label: 'Procédure RH' },
  { value: 'autre', label: 'Autre' },
];

// ─── Formulaire upload ─────────────────────────────────────────────────────────

const UploadForm = ({ onSave, onCancel }) => {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'convention', notes: '' });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    // Auto-remplir le nom depuis le fichier si vide
    if (!form.name) {
      setForm(prev => ({ ...prev, name: f.name.replace(/\.[^.]+$/, '') }));
    }
  };

  const handleSubmit = async () => {
    if (!file) { setError('Veuillez sélectionner un fichier'); return; }
    if (!form.name.trim()) { setError('Nom du document requis'); return; }

    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', form.name.trim());
      fd.append('category', form.category);
      if (form.notes.trim()) fd.append('notes', form.notes.trim());
      await uploadHRDocument(fd);
      onSave();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

  return (
    <div className="border border-primary/20 rounded-xl bg-primary/5 p-5 space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-primary">
        <Upload size={15} />
        Ajouter un document
      </div>

      {/* Zone de drop / sélection */}
      <div
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${
          file ? 'border-primary/40 bg-primary/5' : 'border-gray-200 hover:border-primary/30 hover:bg-gray-50'
        }`}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,image/jpeg,image/png"
          className="hidden"
          onChange={handleFileChange}
        />
        {file ? (
          <div className="space-y-1">
            <FileText size={20} className="mx-auto text-primary" />
            <p className="text-sm font-medium text-gray-800">{file.name}</p>
            <p className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} Mo</p>
          </div>
        ) : (
          <div className="space-y-1 text-gray-400">
            <Upload size={20} className="mx-auto opacity-50" />
            <p className="text-sm">Cliquez pour sélectionner un fichier</p>
            <p className="text-xs">PDF, JPG, PNG — max 10 Mo</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Nom du document *</label>
          <input className={inputCls} value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Ex: Accord d'entreprise 2024" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Catégorie</label>
          <select className={inputCls} value={form.category}
            onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optionnel)</label>
        <input className={inputCls} value={form.notes}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          placeholder="Ex: Version 2024, mise à jour mars 2026" />
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200">
          Annuler
        </button>
        <button onClick={handleSubmit} disabled={uploading}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg disabled:opacity-50">
          {uploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Upload en cours…' : 'Uploader et indexer'}
        </button>
      </div>
    </div>
  );
};

// ─── Ligne document ────────────────────────────────────────────────────────────

const DocRow = ({ doc, onDelete, onReindex }) => {
  const [expanded, setExpanded] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleReindex = async () => {
    setReindexing(true);
    try { await reindexHRDocument(doc.id); onReindex(); } catch {}
    setReindexing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer "${doc.name}" ?`)) return;
    setDeleting(true);
    try { await deleteHRDocument(doc.id); onDelete(); } catch {}
    setDeleting(false);
  };

  const chunkCount = doc._count?.chunks ?? 0;
  const isPdf = doc.mimeType === 'application/pdf';
  const categoryLabel = CATEGORIES.find(c => c.value === doc.category)?.label || doc.category || '—';

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
        <button onClick={() => setExpanded(e => !e)} className="text-gray-400 hover:text-gray-600">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-gray-400 flex-shrink-0" />
            <span className="font-medium text-gray-800 text-sm truncate">{doc.name}</span>
            <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">{categoryLabel}</span>
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-gray-400">
              {chunkCount} chunk{chunkCount !== 1 ? 's' : ''} indexé{chunkCount !== 1 ? 's' : ''}
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
              doc.indexed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              {doc.indexed ? '✓ Indexé' : '⏳ En attente'}
            </span>
            {doc.size && (
              <span className="text-xs text-gray-400">{(doc.size / 1024).toFixed(0)} Ko</span>
            )}
            <span className="text-xs text-gray-400">
              {new Date(doc.uploadedAt).toLocaleDateString('fr-FR')}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Ouvrir le fichier */}
          {doc.fileUrl && (
            <a href={doc.fileUrl} target="_blank" rel="noreferrer"
              className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition"
              title="Ouvrir le fichier">
              <ExternalLink size={14} />
            </a>
          )}
          {/* Ré-indexer (PDF uniquement) */}
          {isPdf && (
            <button onClick={handleReindex} disabled={reindexing} title="Ré-indexer"
              className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition disabled:opacity-40">
              {reindexing ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </button>
          )}
          <button onClick={handleDelete} disabled={deleting} title="Supprimer"
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition disabled:opacity-40">
            {deleting ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
          </button>
        </div>
      </div>

      {expanded && doc.notes && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-2">
          <p className="text-xs text-gray-500 italic">{doc.notes}</p>
        </div>
      )}
    </div>
  );
};

// ─── Composant principal ───────────────────────────────────────────────────────

const OrionDocumentManagement = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getHRDocuments();
      setDocs(res.data || []);
    } catch { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const totalChunks = docs.reduce((acc, d) => acc + (d._count?.chunks || 0), 0);
  const indexedCount = docs.filter(d => d.indexed).length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bot size={16} className="text-primary" />
            Base de connaissance — Orion
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-primary/5 rounded-lg px-4 py-3 text-sm text-primary border border-primary/10">
            <strong>Documents PDF</strong> — Uploadez vos accords d'entreprise, grilles salariales ou tout document RH.
            Orion les consulte pour répondre aux questions des utilisateurs (RAG).
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Documents', value: docs.length },
              { label: 'Chunks indexés', value: totalChunks },
              { label: 'Indexés', value: indexedCount },
            ].map(s => (
              <div key={s.label} className="bg-gray-50 rounded-lg px-3 py-2 text-center">
                <div className="text-lg font-bold text-primary">{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Bouton ajouter */}
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 border border-dashed border-primary/30 rounded-xl text-sm text-primary hover:bg-primary/5 w-full justify-center transition">
              <Upload size={16} />
              Uploader un document
            </button>
          )}

          {/* Formulaire */}
          {showForm && (
            <UploadForm
              onSave={() => { setShowForm(false); fetchDocs(); }}
              onCancel={() => setShowForm(false)}
            />
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}

          {/* Liste */}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader className="animate-spin text-primary" size={24} />
            </div>
          ) : docs.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <Bot size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucun document. Uploadez la convention collective pour commencer.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {docs.map(doc => (
                <DocRow
                  key={doc.id}
                  doc={doc}
                  onDelete={fetchDocs}
                  onReindex={fetchDocs}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info indexation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm text-gray-600">Fonctionnement de l'indexation</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-xs text-gray-600 space-y-2 leading-relaxed">
            <li><span className="font-medium text-gray-700">PDF</span> — Le texte est extrait automatiquement puis découpé en chunks de ~400 mots. Orion consulte les chunks les plus pertinents à chaque question.</li>
            <li><span className="font-medium text-gray-700">Images</span> — Non indexées (pas d'extraction texte), mais consultables via le lien de téléchargement.</li>
            <li><span className="font-medium text-gray-700">Ré-indexer</span> — Si vous remplacez un document ou que l'indexation a échoué (statut "En attente"), cliquez sur le bouton ré-indexer.</li>
            <li><span className="font-medium text-gray-700">Statut "En attente"</span> — L'indexation se fait en arrière-plan, rafraîchissez la page après quelques secondes.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrionDocumentManagement;
